import _ from 'lodash';
import React, { ChangeEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getDocument } from '../../../../api/instant-lease-api';
import Layout from '../../../../layout';
import { setNotification } from '../../../../redux/cache-slice';
import { updateCustomerData } from '../../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import {
  APIPaths,
  Document,
  Partner,
} from '../../../../types/instant-lease-api';
import {
  getAGBLink,
  getAppTypeLowerCase,
  getPartnerText,
  getText,
} from '../../../../utils/getter';
import Navigation from '../../../../utils/navigation';
import FormLayout from '../../../common/form-layout/form-layout';
import { ReactComponent as ContractIcon } from '../../../common/icons/contract-icon.svg';
import { ReactComponent as DownloadIcon } from '../../../common/icons/download-icon.svg';
import { ReactComponent as InsuranceIcon } from '../../../common/icons/insurance-icon.svg';
import Checkbox from '../../components/checkbox/checkbox';
import ButtonGroup from '../../molecules/button-group/button-group';
import './contract.css';

const Contract: React.FC = () => {
  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Auth Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // Loading State
  const [loading, setLoading] = useState<boolean>(false);

  // Leaseing Application Type
  const type = getAppTypeLowerCase(activeApplication?.type);

  // Check if document exists
  const isDocumentExist = (documentType: string): Document | undefined =>
    activeApplication?.registered_documents?.find(
      (doc) => doc.document_type === documentType,
    );

  // Download the requested from API
  const downloadFile = (document: Document | undefined) => {
    if (document) {
      const { mime_type, token, file_name } = document;
      getDocument(accessToken, activeApplication?.uuid || '', token)
        .then((response) => {
          const blob = window.URL.createObjectURL(
            new Blob([response], { type: mime_type }),
          );
          const link = window.document.createElement('a');
          link.href = blob;
          // link.download = file_name;
          link.setAttribute('download', file_name);
          link.click();
          window.open(blob, '_blank');
        })
        .catch(() => {
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: true,
              notificationTitle: 'Das tut uns leid',
              notificationBody: getText('error_unable_to_process_data'),
            }),
          );
        });
    }
  };

  // Disclaimer state
  const AGBAgreementsValue =
    activeApplication?.customer?.agb_agreements?.confirmed?.answer;

  const ALDAgreementsValue =
    activeApplication?.customer?.ald_agreements?.terms_and_conditions?.answer;

  // Insurance agreements value
  const value =
    activeApplication?.customer?.insurance_agreements?.confirmed?.answer;

  // some partner has extra checkbox below the value control
  const [checkboxOneValue, setCheckboxOneValue] = useState<boolean>(
    ALDAgreementsValue || false,
  );

  const [checkboxTwoValue, setCheckboxTwoValue] = useState<boolean>(
    ALDAgreementsValue || false,
  );

  const [checkboxThreeValue, setCheckboxThreeValue] = useState<boolean>(
    ALDAgreementsValue || false,
  );

  const [checkboxFourValue, setCheckboxFourValue] = useState<boolean>(
    ALDAgreementsValue || false,
  );

  // Dispatch hook
  const dispatch = useAppDispatch();

  // history hook
  const navigate = useNavigate();

  // Navigation
  const { nextRoute } = new Navigation();

  // Update Agreements on check
  const updateInsuranceAgreements = (event: ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    dispatch(
      updateCustomerData({
        accessToken,
        leaseApplicationId: activeApplication?.uuid || '',
        inputValue: {
          confirmed: event.target.checked,
        },
        path: APIPaths.INSURANCE_AGREEMENTS,
      }),
    )
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  // Update Agreements on check
  const updateAGBAgreements = (event: ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    dispatch(
      updateCustomerData({
        accessToken,
        leaseApplicationId: activeApplication?.uuid || '',
        inputValue: {
          confirmed: event.target.checked,
        },
        path: APIPaths.UPDATE_AGB_AGREEMENTS,
      }),
    )
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  const hasALDAgreements = () => {
    const membersHasALDAgreements = [
      'mb member',
      'smart_member',
      'member_mb_conf1',
      'member_mb conf2',
      'member_mb_conf3',
      'member_mb conf4',
      'member_mb_conf5',
      'member_mb conf6',
      'member_mb_conf7',
      'member_mb_conf8',
      'member_smart_conf1',
      'member_smart_conf2',
      'member_smart conf3',
      'member_smart_conf4',
      'member_smart_conf5',
      'member_smart_conf6',
      'member smart conf7',
      'member_smart_conf8',
      'corporate_benefits_smart',
      'smart_enpal',
    ];
    return membersHasALDAgreements.includes(
      activeApplication?.loyalty_card?.loyalty_card_type?.toLowerCase() || '',
    );
  };

  const isDisabled = () => {
    if (hasCarInsurance && !value) {
      return true;
    }

    if (!AGBAgreementsValue) {
      return true;
    }

    if (
      [
        checkboxOneValue,
        checkboxTwoValue,
        checkboxThreeValue,
        checkboxFourValue,
      ].includes(false) &&
      hasALDAgreements()
    ) {
      return true;
    }

    return false;
  };

  // if it has car insurance
  const hasCarInsurance = !_.isEmpty(
    activeApplication?.offer?.leasing_options?.insurance,
  );

  const submit = () => {
    setLoading(true);
    dispatch(
      updateCustomerData({
        accessToken,
        leaseApplicationId: activeApplication?.uuid || '',
        inputValue: {
          terms_and_conditions: true,
        },
        path: APIPaths.ALD_AGREEMENTS,
      }),
    )
      .then(() => {
        setLoading(false);
        navigate(nextRoute());
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const contractType =
    activeApplication?.partner === Partner.KIA_FLEX
      ? 'Mietvertrag'
      : 'Leasingvertrag';

  return (
    <Layout
      heading={`Bitte prüfe vorab deinen ${contractType}`}
      stepStatus
      className='contract'
    >
      <FormLayout
        style={{ marginTop: '70px' }}
        sectionTitle='Deine Vertragsunterlagen'
        sectionSubtitle={`Bitte kontrolliere vorab deinen ${contractType} und lade ihn sowie die Versicherungsbedingungen herunter.`}
      >
        <div className='contract-download contract-page'>
          <div
            className='contract'
            role='presentation'
            onClick={() =>
              downloadFile(isDocumentExist('UNSIGNED_LEASING_PROPOSAL'))
            }
          >
            <div>
              <ContractIcon />
              <p className='content-normal'>
                <span className='small-text'>VORLÄUFIGER VERTRAG</span>
                <br />
                <span
                  className='content-bold block max-w-xs! overflow-hidden text-ellipsis whitespace-nowrap'
                  title={
                    activeApplication?.offer?.vehicle_data?.full_name || ''
                  }
                >
                  {activeApplication?.offer?.vehicle_data?.full_name}
                </span>
              </p>
            </div>
            <div className='mobile-hidden'>
              <DownloadIcon />
              <p className='content-bold'>Herunterladen</p>
            </div>
          </div>
        </div>
        {hasCarInsurance && (
          <>
            <h2 className='content-bold' style={{ marginBottom: '16px' }}>
              Deine Serviceunterlagen
            </h2>
            <div className='contract-download contract-page'>
              <div
                className='contract'
                role='presentation'
                style={{ marginTop: '0' }}
                onClick={() =>
                  window.open(
                    getPartnerText(`insurance_link_${type}`),
                    '_blank',
                  )
                }
              >
                <div>
                  <InsuranceIcon />
                  <p className='content-bold'>Versicherungsbedingungen</p>
                </div>
                <div className='mobile-hidden'>
                  <DownloadIcon />
                  <p className='content-bold'>Herunterladen</p>
                </div>
              </div>
            </div>
          </>
        )}
        <Checkbox
          name='disclaimer'
          checked={AGBAgreementsValue}
          type='checkbox'
          square
          checkboxPosition='top'
          onChange={updateAGBAgreements}
        >
          <p className='input-text checkbox-content'>
            Ich akzeptiere die{' '}
            <a
              target='_blank'
              rel='noopener noreferrer'
              role='button'
              href={getAGBLink(activeApplication)}
            >
              Allgemeinen Geschäftsbedingungen (AGB) von Ayvens
            </a>
          </p>
        </Checkbox>
        <div className='checkbox-insurance-box'>
          {hasCarInsurance && (
            <Checkbox
              type='checkbox'
              square
              checkboxPosition='top'
              name='schufa_data'
              style={{ alignSelf: 'baseline' }}
              checked={value}
              onChange={updateInsuranceAgreements}
            >
              <p className='input-text checkbox-content'>
                Ich habe die Versicherungsbedingungen heruntergeladen und
                gelesen.
              </p>
            </Checkbox>
          )}

          {hasALDAgreements() && (
            <>
              <Checkbox
                name='disclaimer'
                checked={checkboxOneValue}
                type='checkbox'
                square
                checkboxPosition='top'
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setCheckboxOneValue(e.target.checked)
                }
                style={{ alignSelf: 'baseline' }}
              >
                <div className='input-text'>
                  Ich akzeptiere die{' '}
                  <a
                    target='_blank'
                    rel='noopener noreferrer'
                    role='button'
                    href='https://de.smart.com/de/service/terms-of-service/'
                  >
                    Allgemeinen Geschäftsbedingungen des Herstellers für
                    digitale Dienste.
                  </a>
                </div>
              </Checkbox>
              <Checkbox
                name='disclaimer'
                checked={checkboxTwoValue}
                type='checkbox'
                square
                checkboxPosition='top'
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setCheckboxTwoValue(e.target.checked)
                }
                style={{ alignSelf: 'baseline' }}
              >
                <div className='input-text'>
                  Ich akzeptiere die{' '}
                  <a
                    target='_blank'
                    rel='noopener noreferrer'
                    role='button'
                    href='https://de.smart.com/de/service/terms-conditions-isp/'
                  >
                    Bedingungen für das integrierte Servicepaket.
                  </a>
                </div>
              </Checkbox>
              <Checkbox
                name='disclaimer'
                checked={checkboxThreeValue}
                type='checkbox'
                square
                checkboxPosition='top'
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setCheckboxThreeValue(e.target.checked)
                }
                style={{ alignSelf: 'baseline' }}
              >
                <div className='input-text'>
                  Ich akzeptiere die{' '}
                  <a
                    target='_blank'
                    rel='noopener noreferrer'
                    role='button'
                    href='https://de.smart.com/de/service/warranty-terms/'
                  >
                    Garantiebedingungen.
                  </a>
                </div>
              </Checkbox>{' '}
              <Checkbox
                name='disclaimer'
                checked={checkboxFourValue}
                type='checkbox'
                square
                checkboxPosition='top'
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setCheckboxFourValue(e.target.checked)
                }
                style={{ alignSelf: 'baseline' }}
              >
                <div className='input-text'>
                  Ich akzeptiere die{' '}
                  <a
                    target='_blank'
                    rel='noopener noreferrer'
                    role='button'
                    href='https://de.smart.com/de/service/terms-of-use/'
                  >
                    Nutzungsbedingungen für smart ID.
                  </a>
                </div>
              </Checkbox>
            </>
          )}
        </div>
      </FormLayout>

      <ButtonGroup
        type='back-next'
        className='back-next medium'
        buttonOneProps={{
          type: 'submit',
          disabled: isDisabled(),
          loading,
          onClick: () => submit(),
          dataTestId: 'next',
          children: 'weiter',
        }}
      />
    </Layout>
  );
};

export default Contract;
